import { GraduationCap, User, Users, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
	type ActionFunctionArgs,
	data,
	useLoaderData,
	useNavigate,
} from 'react-router'
import HeroSection from '#app/components/Landing-page/hero-section'
import {
	ScreeningFlow,
	type ScreeningFlowData,
} from '#app/components/screening/ScreeningFlow'
import {
	DEMO_SCREENING_MODE,
	ScreeningModeProvider,
} from '#app/components/screening/ScreeningModeContext'
import { assets } from '#app/data/assets'
import { content } from '#app/data/content'

// Expose Places to the public demo so address search + "Can't Find Your
// Address? Enter It Manually" match pre-prod. Key is browser-restricted.
export async function loader() {
	return data({
		googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY ?? undefined,
	})
}

type FlowType = 'applicant' | 'guarantor' | 'student'

const DEMO_APPLICATION_ID = 'demo-application'

// ── Demo action ──────────────────────────────────────────────────────────────
//
// The PUBLIC demo at /demo renders the REAL screening step components. Those
// components post their form data to the current route's action (this one). To
// keep the demo side-effect free, this action persists NOTHING and sends no
// mail — it only echoes the `{ success, nextStep, applicationId }` shape the
// step components expect so the flow advances exactly like production.
export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const actionType = formData.get('_action')?.toString()

	// PropertyStep probes for existing applications on mount — none in demo.
	if (actionType === 'check-existing-applications') {
		return data({ existingApplications: [] })
	}

	if (actionType === 'consent') {
		return data({
			success: true,
			nextStep: 1,
			applicationId: DEMO_APPLICATION_ID,
		})
	}

	// Mirror the real action's step→next mapping so Property/Rental/Employment
	// navigate forward identically. ID-verification and Verify-income are
	// rendered by simulated demo variants and never post here.
	const stepNameToNumber: Record<string, number> = {
		property: 1,
		'id-verification': 2,
		rental: 3,
		employment: 4,
		'verify-income': 5,
		review: 6,
	}
	const TOTAL_STEPS = 6
	const currentStepNumber = stepNameToNumber[actionType ?? ''] ?? 1
	const editMode = formData.get('editMode')?.toString() === 'true'
	// Student housing: Canadian Bank Account popup on Employment → No skips
	// Verify Income and lands on Review (same as live screening).
	const skipVerifyIncome =
		actionType === 'employment' &&
		formData.get('skipVerifyIncome')?.toString() === 'true'
	// Same rule as live screening: a previous Bank=No skip must return to
	// Verify Income when employment is saved without skipping again.
	const incomeWasSkipped =
		actionType === 'employment' &&
		formData.get('incomeWasSkipped')?.toString() === 'true'
	// Demo keeps the full step path: Property → ID → Rental → Employment →
	// Verify Income → Review. Review still uses fixture data (not a skip).
	const nextStep = skipVerifyIncome
		? 6
		: editMode && !incomeWasSkipped
			? 6
			: Math.min(currentStepNumber + 1, TOTAL_STEPS)

	return data({
		success: true,
		nextStep,
		applicationId: DEMO_APPLICATION_ID,
		...(skipVerifyIncome ? { skipVerifyIncome: true } : {}),
		...(actionType === 'employment' && !skipVerifyIncome
			? { clearVerifyIncomeSkip: true }
			: {}),
	})
}

// ── Mock data ────────────────────────────────────────────────────────────────
// Hand-authored data that mimics what the live loader pulls from the DB, so the
// real step components render fully prefilled. Edit these to change the demo.

const DEMO_PROPERTY = {
	id: 'demo-property',
	title: 'CN Tower Residences',
	address: '290 Bremner Blvd, Toronto, ON',
	image: '/img/demo_property.jpeg',
	images: ['/img/demo_property.jpeg'],
	agentId: 'toronto',
	location: 'Toronto',
	// Multi-family so student Unit & Bed inventory path is enabled.
	propertyType: 'multi-family',
	amenities: ['Parking Available', 'Gym', 'Laundry In Building'],
	smokingPolicy: 'no-smoking',
	petPolicy: 'Pets Allowed',
	parkingPolicy: 'Available At Additional Cost',
	parkingCost: 150,
	utilitiesResponsibility: null,
	utilitiesPaidByTenant: [],
}

const DEMO_STEP_DATA: Record<string, Record<string, unknown>> = {
	property: {
		propertyId: 'demo-property',
		propertyName: 'CN Tower Residences',
		address: '290 Bremner Blvd, Toronto, ON',
		city: 'Toronto',
		images: ['/img/demo_property.jpeg'],
		// Doc: Move-In Date May 31, 2026
		moveInDate: '2026-05-31',
		bedrooms: '2',
		bathrooms: '2',
		unitNumber: '6',
		numAdults: 2,
		numChildren: 1,
		totalOccupants: 3,
		adults: [
			{
				id: 'adult-1',
				firstName: 'John',
				lastName: 'Carter',
				name: 'John Carter',
				email: 'john.carter@truetenant.ai',
				phone: '+14169671111',
				phoneCountryCode: 'CA',
				// Doc: December 31, 1992
				dateOfBirth: '1992-12-31',
				isPrimary: true,
			},
			{
				id: 'adult-2',
				firstName: 'Sarah',
				lastName: 'Carter',
				name: 'Sarah Carter',
				email: 'sarah.carter@truetenant.ai',
				phone: '+14165550199',
				phoneCountryCode: 'CA',
				dateOfBirth: '1995-06-18',
				relationship: 'Partner',
			},
		],
		// Doc: Child 1 DOB March 13, 2020
		children: [{ id: 'child-1', dateOfBirth: '2020-03-13' }],
		hasGuarantor: 'yes',
		guarantor: {
			firstName: 'Robert',
			lastName: 'Carter',
			name: 'Robert Carter',
			email: 'robert.carter@truetenant.ai',
			phone: '+19053333333',
			phoneCountryCode: 'CA',
			relationship: 'Parent',
			dateOfBirth: '1992-12-31',
		},
		totalPets: 1,
		hasPet: 'yes',
		pets: [
			{
				id: 'pet-1',
				petType: 'dog',
				// Closest UI option to doc "15 - 30 lbs" (see open Q)
				weight: '10-30 lbs',
				breed: 'Labrador',
			},
		],
		totalVehicles: 1,
		hasVehicle: 'yes',
		vehicles: [
			{
				id: 'vehicle-1',
				vehicleType: 'car',
				registration: 'CABC123',
				makeModel: 'Toyota Camry',
				color: 'White',
			},
		],
	},
	rental: {
		// Accordion opens with no status; `_demoPrefill` restores related fields
		// when the user picks a living situation (same as previous /demo).
		rentalStatus: '',
		_demoPrefill: true,
		currentLandlordName: 'Michael Thompson',
		currentLandlordPhone: '+14163334444',
		currentLandlordPhoneCountryCode: 'CA',
		currentLandlordEmail: 'michaellandlord@truetenant.ai',
		currentAddress: '55 Harbour Square',
		currentAddressCity: 'Toronto',
		currentAddressProvince: 'ON',
		currentAddressPostalCode: 'M5J 2L1',
		currentRent: '3200',
		currentPaymentMethod: 'Credit Card',
		currentYearsLived: '3 Years 4 Months',
		previousLandlordName: 'Jennifer Lewis',
		previousLandlordPhone: '+16475550123',
		previousLandlordPhoneCountryCode: 'CA',
		previousLandlordEmail: 'jenniferlandlord@truetenant.ai',
		previousAddress: '100 Queens Quay E',
		previousAddressCity: 'Toronto',
		previousAddressProvince: 'ON',
		previousAddressPostalCode: 'M5E 1V3',
		previousRent: '2600',
		previousPaymentMethod: 'Credit Card',
		// Doc: Move-In & Move-Out December 2018 to February 2022
		previousMoveInDate: '2018-12-01',
		previousMoveOutDate: '2022-02-01',
		previousDurationLived: '3 Years 2 Months',
		// N4/L1 removed from rental UI (doc); keep empty so they are not re-submitted.
		receivedN4: '',
		receivedL1: '',
		// Student Additional Questions — dummy answers are No (no explain reveals).
		everEvicted: 'no',
		everEvictedExplanation: '',
		everConvictedFelony: 'no',
		everConvictedFelonyExplanation: '',
		doesSmoke: 'no',
		newToCountry: 'no',
		// Work Authorization moved to Employment (doc); rental no longer seeds it.
		immigrationStatus: '',
		// First-time-renter (applicant) / "Other" (guarantor) living details.
		currentAddressFirstRenter: '55 Harbour Square',
		currentAddressFirstRenterCity: 'Toronto',
		currentAddressFirstRenterProvince: 'ON',
		currentAddressFirstRenterPostalCode: 'M5J 2L1',
		newToCountryFirstRenter: 'no',
		currentLivingSituation: 'Parents / Family',
		yearsLivedAtAddress: '3 Years 4 Months',
		immigrationStatusFirstRenter: '',
		// "Own Home" (guarantor) living details.
		ownHomeAddress: '55 Harbour Square',
		ownHomeAddressCity: 'Toronto',
		ownHomeAddressProvince: 'ON',
		ownHomeAddressPostalCode: 'M5J 2L1',
		ownHomeYearsLived: '3 Years 4 Months',
		ownHomeImmigrationStatus: 'Canadian Citizen',
		// Other living situation extras (shown when Yes is selected).
		rentingRoom: 'no',
		rentingRoomRent: '1200',
		rentingRoomPaymentMethod: 'E-Transfer',
		rentingRoomDuration: '1 Year 6 Months',
		notOnLease: 'no',
		notOnLeaseRent: '900',
		notOnLeasePaymentMethod: 'E-Transfer',
		notOnLeaseDuration: '1 Year 0 Months',
	},
	employment: {
		// No status selected up front — `_demoPrefill` keeps these fields so
		// picking a status reveals the related prefilled data.
		employmentStatus: '',
		// Currently employed (current job).
		companyName: 'TrueTenant',
		occupation: 'Software Engineer',
		annualIncome: '150000',
		companyPhone: '+14373334444',
		companyPhoneCountryCode: 'CA',
		workAddress: '250 University Ave',
		workAddressCity: 'Toronto',
		workAddressProvince: 'ON',
		workAddressPostalCode: 'M5H 3E5',
		yearsOfEmployment: '4 Years',
		employmentType: 'Permanent',
		maternityLeave: 'no',
		// Doc: remove Government Assistance from all branches.
		governmentAssistance: '',
		// Work Authorization (moved from Rental). Expiration is hidden while
		// Citizen/PR/refugee; value seeds the date when a visa/permit is picked.
		immigrationStatus: 'Canadian Citizen',
		immigrationStatusExpiration: '2028-08-31',
		// No additional rows up front — "+ Additional Employment" reveals Nova
		// Retail from `_demoPrefill.additionalEmployment`.
		additionalEmployments: [],
		_demoPrefill: {
			// Revealed when Maternity/Paternity Leave → Yes.
			maternityLeaveFrom: '2025-06-15',
			additionalEmployment: {
				companyName: 'Nova Retail Group',
				occupation: 'Sales Associate',
				annualIncome: '28000',
				companyPhone: '+14165550177',
				workAddress: '220 Yonge St',
				workAddressCity: 'Toronto',
				workAddressProvince: 'ON',
				workAddressPostalCode: 'M5B 2H1',
				yearsOfEmployment: '1 Year',
				employmentType: 'Part-Time',
				sourceOfIncome: '',
			},
		},
		// Previous employment history (shown under "Not Employed").
		previousCompanyName: 'TrueTenant',
		previousOccupation: 'Software Developer',
		previousAnnualIncome: '100000',
		previousCompanyPhone: '+16135550142',
		previousCompanyPhoneCountryCode: 'CA',
		previousWorkAddress: '150 Elgin St',
		previousWorkAddressCity: 'Ottawa',
		previousWorkAddressProvince: 'ON',
		previousWorkAddressPostalCode: 'K2P 1L4',
		// Doc: First & Last Day Of Work April 2018 to April 2022
		previousEmploymentStartDate: '2018-04-01',
		previousEmploymentEndDate: '2022-04-01',
		previousYearsOfEmployment: '4 Years',
		previousEmploymentType: 'Permanent',
		previousSourceOfIncome: 'Investment Income',
		notEmployedGovernmentAssistance: '',
		// No employment history — living on investment income.
		sourceOfIncome: 'Investment Income',
		noHistoryAnnualIncome: '100000',
		noHistoryGovernmentAssistance: '',
	},
}

// ── Applicant / guarantor property step seeds ────────────────────────────────
// For the APPLICANT demo only, the household starts collapsed (1 adult, 0
// children, no guarantor / pets / vehicles). The "real" values live in
// `_demoPrefill` and are revealed as the user increments the stepper or toggles
// "Yes" — so the demo shows the app filling those fields in for them. The
// guarantor flow keeps the FULL `DEMO_STEP_DATA.property` (its read-only
// summary of the primary applicant depends on it), so this collapsing is
// scoped to the applicant's own property step.
const DEMO_FULL_PROPERTY = DEMO_STEP_DATA.property
const DEMO_FULL_ADULTS = DEMO_FULL_PROPERTY.adults as Array<
	Record<string, unknown>
>
const DEMO_FULL_CHILDREN = DEMO_FULL_PROPERTY.children as Array<
	Record<string, unknown>
>
const DEMO_FULL_VEHICLES = DEMO_FULL_PROPERTY.vehicles as Array<
	Record<string, unknown>
>

const DEMO_PROPERTY_PREFILL = {
	// Index-aligned with the adults array (index 1 = the second adult).
	adults: DEMO_FULL_ADULTS,
	children: DEMO_FULL_CHILDREN,
	// Weight/breed are only surfaced once a Dog/Cat/Reptile type is picked.
	petWeight: '10-30 lbs',
	petBreed: 'Labrador',
	petDetailsByType: {
		dog: { weight: '10-30 lbs', breed: 'Labrador' },
		cat: { weight: '10-30 lbs', breed: 'Persian' },
		reptile: { weight: '10-30 lbs', breed: 'Bearded Dragon' },
	},
	// Registration (+ make/model/color) only when Car / SUV is picked.
	vehicleRegistration: 'CABC123',
	vehicleMakeModel: 'Toyota Camry',
	vehicleColor: 'White',
}

const DEMO_APPLICANT_PROPERTY_STEP: Record<string, unknown> = {
	...DEMO_FULL_PROPERTY,
	// Collapsed household — only the primary applicant is present up front.
	numAdults: 1,
	numChildren: 0,
	totalOccupants: 1,
	adults: [DEMO_FULL_ADULTS[0]],
	children: [],
	// Guarantor object stays in state but hidden; toggling "Yes" reveals it
	// already prefilled (no reveal handler needed for it).
	hasGuarantor: 'no',
	totalPets: 0,
	hasPet: 'no',
	pets: [],
	totalVehicles: 0,
	hasVehicle: 'no',
	vehicles: [],
	// Values PropertyStep pulls from as the user expands each section.
	_demoPrefill: DEMO_PROPERTY_PREFILL,
}

// In the guarantor flow the property step shows the primary applicant's household
// read-only (from `primaryApplicantInfo.occupancy`) plus the guarantor's OWN
// details, which are driven by `adults[0]`. Seed that slot with the guarantor
// persona so "Your Details" — including Relationship = Parent — is prefilled.
const DEMO_GUARANTOR_SELF = DEMO_FULL_PROPERTY.guarantor as Record<
	string,
	unknown
>
const DEMO_GUARANTOR_PROPERTY_STEP: Record<string, unknown> = {
	...DEMO_FULL_PROPERTY,
	numAdults: 1,
	adults: [{ id: 'guarantor-self', ...DEMO_GUARANTOR_SELF }],
}

// ── Review datasets ───────────────────────────────────────────────────────────
// The demo persists nothing, so the Review step can't reflect what the user
// actually typed. It instead shows a COMPLETE dataset: the full household plus
// the rental/employment sections with a concrete status selected (the steps
// themselves open collapsed / unselected, so their own stepData can't drive a
// populated review). Kept separate from the step data so the steps stay
// collapsed while the review reads as a finished application.
// Doc: Income Verification accordion — one verified TD row; "+ Add" connects
// another account via demo Flinks (nothing persisted).
const DEMO_VERIFY_INCOME: Record<string, unknown> = {
	accounts: [
		{
			flinksLoginId: 'demo-td-login',
			status: 'success',
			isPrimary: true,
			verifiedAt: '2025-06-15T12:00:00.000Z',
			summary: {
				institution: 'TD Canada Trust',
				maskedAccountNumber: '****1234',
				accountTitle: 'Chequing',
			},
		},
	],
}

const DEMO_APPLICANT_REVIEW_STEP_DATA: Record<
	string,
	Record<string, unknown>
> = {
	property: DEMO_FULL_PROPERTY,
	rental: { ...DEMO_STEP_DATA.rental, rentalStatus: 'currently-renting' },
	employment: {
		...DEMO_STEP_DATA.employment,
		employmentStatus: 'currently-employed',
	},
	'verify-income': DEMO_VERIFY_INCOME,
}

// Guarantor review: the property card renders the primary applicant's occupancy;
// `property` here only drives the guarantor's own "Your Details" block.
const DEMO_GUARANTOR_REVIEW_STEP_DATA: Record<
	string,
	Record<string, unknown>
> = {
	property: DEMO_GUARANTOR_PROPERTY_STEP,
	rental: { ...DEMO_STEP_DATA.rental, rentalStatus: 'currently-renting' },
	employment: {
		...DEMO_STEP_DATA.employment,
		employmentStatus: 'currently-employed',
	},
	'verify-income': DEMO_VERIFY_INCOME,
}

// ── Student housing demo seeds ───────────────────────────────────────────────
// Same collapse + reveal pattern as the standard applicant demo; only ADD
// student-only fields (school/program, gender, multi-guarantors, unit+bed).
// Doc: single Occupant Details only — no Adult 2 / children in student flow.
const DEMO_STUDENT_ADULTS = [
	{
		...DEMO_FULL_ADULTS[0],
		// Doc: September 12, 2004 (student-appropriate)
		dateOfBirth: '2004-09-12',
		gender: 'Male',
		// Doc: University of Toronto Mississauga, Bachelor of Commerce.
		// Catalog program option nearest match: Business, Commerce And Management.
		schoolProgram:
			'University of Toronto Mississauga, Business, Commerce And Management',
		studyLevelAndGraduation: "Undergraduate (Bachelor's), April 2028",
	},
]

const DEMO_STUDENT_GUARANTOR = DEMO_FULL_PROPERTY.guarantor as Record<
	string,
	unknown
>

const DEMO_STUDENT_GUARANTORS = [
	{
		id: 'student-guarantor-1',
		firstName: DEMO_STUDENT_GUARANTOR.firstName,
		lastName: DEMO_STUDENT_GUARANTOR.lastName,
		name: DEMO_STUDENT_GUARANTOR.name,
		email: DEMO_STUDENT_GUARANTOR.email,
		phone: DEMO_STUDENT_GUARANTOR.phone,
		phoneCountryCode: DEMO_STUDENT_GUARANTOR.phoneCountryCode ?? 'CA',
		relationship: DEMO_STUDENT_GUARANTOR.relationship ?? 'Parent',
		linkedOccupantIds: ['adult-1'],
	},
]

const DEMO_STUDENT_FULL_PROPERTY: Record<string, unknown> = {
	...DEMO_FULL_PROPERTY,
	isStudentHousing: true,
	unitId: 'demo-unit-6',
	unitNumber: '6',
	// Doc: Unit & Bed → 6 · Bed A (selected by default)
	bedId: 'demo-bed-a',
	bedLabel: 'Bed A',
	moveInDateFlexible: 'yes',
	numAdults: 1,
	numChildren: 0,
	totalOccupants: 1,
	adults: DEMO_STUDENT_ADULTS,
	children: [],
	guarantors: DEMO_STUDENT_GUARANTORS,
}

// Collapsed student property step — primary adult + always-on guarantor;
// pets / vehicles reveal from `_demoPrefill`. Guarantor has no Yes/No gate.
const DEMO_STUDENT_PROPERTY_STEP: Record<string, unknown> = {
	...DEMO_STUDENT_FULL_PROPERTY,
	numAdults: 1,
	numChildren: 0,
	totalOccupants: 1,
	adults: [DEMO_STUDENT_ADULTS[0]],
	children: [],
	hasGuarantor: 'yes',
	guarantors: DEMO_STUDENT_GUARANTORS,
	totalPets: 0,
	hasPet: 'no',
	pets: [],
	totalVehicles: 0,
	hasVehicle: 'no',
	vehicles: [],
	_demoPrefill: {
		...DEMO_PROPERTY_PREFILL,
		adults: DEMO_STUDENT_ADULTS,
		guarantors: DEMO_STUDENT_GUARANTORS,
	},
}

const DEMO_STUDENT_EMPLOYMENT: Record<string, unknown> = {
	...DEMO_STEP_DATA.employment,
	// Keep employment `_demoPrefill` (additional employment seed) from spread.
	// Work Authorization defaults to Canadian Citizen → Canadian bank popup on
	// Next. Expiration stays hidden for Citizen; value seeds Aug 31, 2028 when
	// the user switches to a visa/permit (EmploymentStep demo seed).
	immigrationStatus: 'Canadian Citizen',
	immigrationStatusExpiration: '2028-08-31',
	// Doc: no Maternity/Paternity Leave in student flow — clear answer seed.
	maternityLeave: '',
	// Default No; detail rows/fields reveal when the user picks Yes.
	receivingStudentFinancialAssistance: 'no',
	studentFinancialAssistance: [
		{
			id: 'demo-aid-1',
			type: 'Scholarship',
			grossIncome: '8000',
			frequency: 'Yearly',
		},
	],
	receivingParentalAssistance: 'no',
	parentalAssistanceSource: 'Parental Assistance',
	parentalAssistanceGrossAmount: '1500',
	parentalAssistanceFrequency: 'Monthly',
}

const DEMO_STUDENT_REVIEW_STEP_DATA: Record<string, Record<string, unknown>> = {
	property: DEMO_STUDENT_FULL_PROPERTY,
	rental: { ...DEMO_STEP_DATA.rental, rentalStatus: 'currently-renting' },
	employment: {
		...DEMO_STUDENT_EMPLOYMENT,
		employmentStatus: 'currently-employed',
	},
	'verify-income': DEMO_VERIFY_INCOME,
}

const DEMO_STEP_NUMBER_TO_NAME: Record<number, string> = {
	1: 'property',
	2: 'id-verification',
	3: 'rental',
	4: 'employment',
	5: 'verify-income',
	6: 'review',
}

function buildDemoData(
	flowType: FlowType,
	googleMapsApiKey?: string,
): ScreeningFlowData {
	const isGuarantor = flowType === 'guarantor'
	const isStudent = flowType === 'student'

	if (isStudent) {
		return {
			userId: 'demo-user',
			applicationId: DEMO_APPLICATION_ID,
			currentStep: 1,
			stepNumberToName: DEMO_STEP_NUMBER_TO_NAME,
			propertyGroups: [
				{
					agent: { id: 'toronto', name: 'Toronto', location: 'Toronto' },
					properties: [DEMO_PROPERTY],
				},
			],
			properties: [DEMO_PROPERTY],
			propertyUnits: {
				'demo-property': [
					{
						id: 'demo-unit-6',
						unitNumber: '6',
						bedrooms: 2,
						bathrooms: 2,
						beds: [
							{ id: 'demo-bed-a', label: 'Bed A' },
							{ id: 'demo-bed-b', label: 'Bed B' },
						],
					},
				],
			},
			stepData: {
				...DEMO_STEP_DATA,
				property: DEMO_STUDENT_PROPERTY_STEP,
				employment: DEMO_STUDENT_EMPLOYMENT,
			},
			reviewStepData: DEMO_STUDENT_REVIEW_STEP_DATA,
			hasConsent: false,
			completedSteps: [],
			applicationStatus: null,
			applicationType: 'primary',
			googleMapsApiKey,
			primaryApplicantInfo: null,
			incomeReassuranceCallTriggered: false,
			viewerName: 'John Carter',
			formKind: 'student',
			isStudentHousing: true,
		}
	}

	return {
		userId: 'demo-user',
		applicationId: DEMO_APPLICATION_ID,
		currentStep: 1,
		stepNumberToName: DEMO_STEP_NUMBER_TO_NAME,
		propertyGroups: [
			{
				agent: { id: 'toronto', name: 'Toronto', location: 'Toronto' },
				properties: [DEMO_PROPERTY],
			},
		],
		properties: [DEMO_PROPERTY],
		propertyUnits: {
			'demo-property': [
				{ id: 'demo-unit-6', unitNumber: '6', bedrooms: 2, bathrooms: 2 },
			],
		},
		// Applicant sees the collapsed property step (reveal-on-demand); the
		// guarantor gets the "Your Details" slot seeded with the guarantor persona.
		stepData: isGuarantor
			? { ...DEMO_STEP_DATA, property: DEMO_GUARANTOR_PROPERTY_STEP }
			: { ...DEMO_STEP_DATA, property: DEMO_APPLICANT_PROPERTY_STEP },
		reviewStepData: isGuarantor
			? DEMO_GUARANTOR_REVIEW_STEP_DATA
			: DEMO_APPLICANT_REVIEW_STEP_DATA,
		hasConsent: false,
		completedSteps: [],
		applicationStatus: null,
		applicationType: isGuarantor ? 'guarantor' : 'primary',
		googleMapsApiKey,
		primaryApplicantInfo: isGuarantor
			? {
					occupancy: DEMO_STEP_DATA.property,
					name: 'John Carter',
					propertyName: 'CN Tower Residences',
					propertyAddress: '290 Bremner Blvd, Toronto, ON',
				}
			: null,
		incomeReassuranceCallTriggered: false,
		viewerName: isGuarantor ? 'Robert Carter' : 'John Carter',
		formKind: 'standard',
		isStudentHousing: false,
	}
}

// ── Generating report (animated hand-off to /demo-report) ─────────────────────

function GeneratingReportScreen({ reportVariant }: { reportVariant: string }) {
	const navigate = useNavigate()
	const [currentStep, setCurrentStep] = useState(0)

	const steps = [
		'Submitting application…',
		'Running AI analysis…',
		'Verifying income data…',
		'Generating screening report…',
		'Report ready!',
	]

	useEffect(() => {
		let i = 0
		const interval = setInterval(() => {
			i += 1
			setCurrentStep(i)
			if (i >= steps.length - 1) {
				clearInterval(interval)
				setTimeout(() => {
					navigate(`/demo-report?variant=${reportVariant}`)
				}, 600)
			}
		}, 700)
		return () => clearInterval(interval)
	}, [navigate, reportVariant])

	return (
		<div className="flex min-h-screen items-center justify-center px-4">
			<div className="mx-auto max-w-sm py-12 text-center">
				{/* Spinner */}
				<div className="mx-auto mb-8 h-16 w-16 animate-spin rounded-full border-4 border-[#E8F0F0] border-t-[#32302F]" />

				<h1 className="mb-6 text-xl font-semibold text-[#32302F]">
					Generating Your Report
				</h1>

				<div className="space-y-3 text-left">
					{steps.map((label, i) => {
						const done = i < currentStep
						const active = i === currentStep
						return (
							<div key={label} className="flex items-center gap-3">
								<div
									className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors ${
										done
											? 'bg-[#32302F]'
											: active
												? 'animate-pulse bg-[#32302F]'
												: 'bg-gray-200'
									}`}
								>
									{done && (
										<svg
											width="10"
											height="8"
											viewBox="0 0 10 8"
											fill="none"
											aria-hidden="true"
										>
											<path
												d="M1 4L3.5 6.5L9 1"
												stroke="white"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									)}
								</div>
								<span
									className={`text-sm ${
										done
											? 'font-medium text-[#32302F]'
											: active
												? 'text-[#32302F]'
												: 'text-[#CCCCCC]'
									}`}
								>
									{label}
								</span>
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}

// ── Flow selection (applicant / guarantor / student) ──────────────────────────

function FlowSelectModal({
	onSelect,
	onClose,
}: {
	onSelect: (flow: FlowType) => void
	onClose: () => void
}) {
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/50 p-4 backdrop-blur-sm">
			<div className="w-full max-w-3xl rounded-2xl border border-[#F3F3F3] bg-white shadow-xl">
				<div className="flex items-start justify-between px-6 pt-6 pb-2">
					<div>
						<h2 className="text-[22px] font-semibold text-[#32302F]">
							How are you applying?
						</h2>
						<p className="mt-1 text-sm text-[#4F4F4F]">
							Choose the flow you'd like to preview.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="mt-1 rounded-full p-1 hover:bg-gray-100"
					>
						<X className="h-5 w-5 text-[#4F4F4F]" />
					</button>
				</div>
				<div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-3">
					<button
						type="button"
						onClick={() => onSelect('applicant')}
						className="flex flex-col items-start gap-3 rounded-[20px] border border-[#E9E9E9] p-5 text-left transition-colors hover:border-[#32302F]"
					>
						<span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F4F8F8]">
							<User className="h-6 w-6 text-[#32302F]" />
						</span>
						<span className="text-[17px] font-medium text-[#32302F]">
							Apply as Applicant
						</span>
						<span className="text-[13px] text-[#747474]">
							Standard template — property, household, ID, rental, employment
							and income. Ends with your pre-screening report.
						</span>
					</button>
					<button
						type="button"
						onClick={() => onSelect('student')}
						className="flex flex-col items-start gap-3 rounded-[20px] border border-[#E9E9E9] p-5 text-left transition-colors hover:border-[#32302F]"
					>
						<span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F4F8F8]">
							<GraduationCap className="h-6 w-6 text-[#32302F]" />
						</span>
						<span className="text-[17px] font-medium text-[#32302F]">
							Apply as Student
						</span>
						<span className="text-[13px] text-[#747474]">
							Student housing template — school/program, unit &amp; bed,
							guarantors, and student assistance questions.
						</span>
					</button>
					<button
						type="button"
						onClick={() => onSelect('guarantor')}
						className="flex flex-col items-start gap-3 rounded-[20px] border border-[#E9E9E9] p-5 text-left transition-colors hover:border-[#32302F]"
					>
						<span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F4F8F8]">
							<Users className="h-6 w-6 text-[#32302F]" />
						</span>
						<span className="text-[17px] font-medium text-[#32302F]">
							Apply as Guarantor
						</span>
						<span className="text-[13px] text-[#747474]">
							Back an applicant — your own ID, living history, employment and
							income. Ends with your guarantor report.
						</span>
					</button>
				</div>
			</div>
		</div>
	)
}

// ── Main component ─────────────────────────────────────────────────────────────

type DemoPhase = 'landing' | 'flow' | 'report'

export default function Demo() {
	const { googleMapsApiKey } = useLoaderData<typeof loader>()
	const [phase, setPhase] = useState<DemoPhase>('landing')
	const [showFlowSelect, setShowFlowSelect] = useState(false)
	const [flowType, setFlowType] = useState<FlowType>('applicant')

	const isGuarantor = flowType === 'guarantor'
	const reportVariant = isGuarantor ? 'guarantor' : 'prescreen'

	if (phase === 'report') {
		return (
			<div className="min-h-screen bg-white">
				<style>{':root { --navbar-height: 0px; }'}</style>
				<GeneratingReportScreen reportVariant={reportVariant} />
			</div>
		)
	}

	if (phase === 'flow') {
		return (
			<div className="min-h-screen bg-white">
				<style>{':root { --navbar-height: 0px; }'}</style>
				<ScreeningModeProvider
					value={{
						...DEMO_SCREENING_MODE,
						onReviewSubmitted: () => {
							window.scrollTo({ top: 0, behavior: 'instant' })
							setPhase('report')
						},
					}}
				>
					<ScreeningFlow {...buildDemoData(flowType, googleMapsApiKey)} />
				</ScreeningModeProvider>
			</div>
		)
	}

	return (
		<div className="min-h-screen bg-white">
			<style>{':root { --navbar-height: 0px; }'}</style>
			<HeroSection
				title={content.marketing.home.hero.title}
				buttonText={content.marketing.home.hero.buttonText}
				backgroundImage={assets.hero.banner}
				onButtonClick={() => setShowFlowSelect(true)}
			/>

			{showFlowSelect && (
				<FlowSelectModal
					onSelect={(flow) => {
						setFlowType(flow)
						setShowFlowSelect(false)
						window.scrollTo({ top: 0, behavior: 'instant' })
						setPhase('flow')
					}}
					onClose={() => setShowFlowSelect(false)}
				/>
			)}
		</div>
	)
}
